import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from './ui/tabs';
import { Switch } from './ui/switch';
import {
    AlertTriangle,
    CheckCircle,
    Clock,
    Loader2,
    Mail,
    RefreshCcw,
} from 'lucide-react';

/**
 * Preview-and-edit dialog for the admin-only NCB clearance reminder
 * flow. Opens from either the NCB Clearance section inside an order
 * or the Needs Attention menu — both call the same backend endpoints
 * (`/api/orders/:id/ncb-reminder/preview` → `POST .../ncb-reminder`).
 *
 * Behaviour contract:
 *   1. Fetch the server-rendered preview on open so the admin edits
 *      exactly what will be sent (recipient, subject, html + plain
 *      bodies). All real order data (dates, catalogue, title) comes
 *      from the backend — the dialog never fabricates fields.
 *   2. If the server reports a reminder was sent within the 24 h
 *      duplicate-guard window, the Send button is DISABLED. Only an
 *      explicit "Override" toggle unblocks it, and that override is
 *      posted to the server which re-validates.
 *   3. Clicking "Approve & Send" requires an explicit second click —
 *      no auto-send on open.
 */
export function NCBReminderDialog({ open, onOpenChange, orderId, orderNumber, orderTitle, onSent }) {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [preview, setPreview] = useState(null);
    const [recipient, setRecipient] = useState('');
    const [subject, setSubject] = useState('');
    const [htmlBody, setHtmlBody] = useState('');
    const [plainBody, setPlainBody] = useState('');
    const [override, setOverride] = useState(false);
    const [error, setError] = useState(null);

    const loadPreview = useCallback(async () => {
        if (!orderId) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/orders/${orderId}/ncb-reminder/preview`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setPreview(res.data);
            setRecipient(res.data.recipient || '');
            setSubject(res.data.subject || '');
            setHtmlBody(res.data.html_body || '');
            setPlainBody(res.data.plain_body || '');
            // Default override OFF — admin must opt in each open.
            setOverride(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to load preview';
            setError(msg);
            setPreview(null);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        if (open) loadPreview();
        // When the dialog closes we deliberately keep the last preview
        // in state — reopening the same order is cheap and the server
        // call still runs.
    }, [open, loadPreview]);

    const guard = preview?.duplicate_guard;
    const blockedByGuard = !!(guard?.within_window && !override);
    const canSend = !loading && !sending && !!recipient && !!subject && !blockedByGuard;

    const formatRelative = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            const diffH = (Date.now() - d.getTime()) / 3600_000;
            if (diffH < 1) return `${Math.max(1, Math.round(diffH * 60))} min ago`;
            if (diffH < 24) return `${Math.round(diffH)} h ago`;
            return `${Math.round(diffH / 24)} d ago`;
        } catch { return ''; }
    };

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${API_BASE}/api/orders/${orderId}/ncb-reminder`,
                {
                    recipient,
                    subject,
                    html_body: htmlBody,
                    plain_body: plainBody,
                    override_duplicate_guard: override,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success(`Reminder sent to ${res.data?.recipient || recipient}`);
            if (onSent) onSent(res.data);
            onOpenChange(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to send reminder';
            setError(msg);
            toast.error(msg);
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                data-testid="ncb-reminder-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-amber-500" />
                        Send NCB clearance reminder
                        {orderNumber && (
                            <span className="text-sm font-normal text-slate-500">
                                — Order #{orderNumber}{orderTitle ? ` · ${orderTitle}` : ''}
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Review the message, edit anything you want, then approve to send. The reminder
                        does not change NCB status. Sends go through the regular email log.
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading preview…
                    </div>
                )}

                {!loading && error && !preview && (
                    <Alert variant="destructive" data-testid="ncb-reminder-dialog-error">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {!loading && preview && (
                    <div className="space-y-4">
                        {guard?.last_sent_at && (
                            <Alert
                                className={
                                    guard.within_window
                                        ? 'border-red-200 bg-red-50'
                                        : 'border-slate-200 bg-slate-50'
                                }
                                data-testid="ncb-reminder-last-sent"
                            >
                                {guard.within_window ? (
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                ) : (
                                    <Clock className="h-4 w-4 text-slate-500" />
                                )}
                                <AlertDescription className="text-xs">
                                    <strong>Last reminder:</strong>{' '}
                                    {formatRelative(guard.last_sent_at)}
                                    {guard.last_sent_by && <> by {guard.last_sent_by}</>}
                                    {guard.last_recipient && <> → {guard.last_recipient}</>}
                                    . Total sent for this order: {guard.total_sent}.
                                    {guard.within_window && (
                                        <> This is within the {guard.window_hours}-hour duplicate guard —
                                            enable the override toggle below to send another.</>
                                    )}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="ncb-reminder-recipient">Recipient</Label>
                            <Input
                                id="ncb-reminder-recipient"
                                data-testid="ncb-reminder-recipient-input"
                                value={recipient}
                                onChange={(e) => setRecipient(e.target.value)}
                                placeholder="customer@example.com"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="ncb-reminder-subject">Subject</Label>
                            <Input
                                id="ncb-reminder-subject"
                                data-testid="ncb-reminder-subject-input"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </div>

                        <Tabs defaultValue="plain" className="w-full">
                            <TabsList>
                                <TabsTrigger value="plain" data-testid="ncb-reminder-plain-tab">
                                    Plain text (editable)
                                </TabsTrigger>
                                <TabsTrigger value="html" data-testid="ncb-reminder-html-tab">
                                    HTML (editable)
                                </TabsTrigger>
                                <TabsTrigger value="preview" data-testid="ncb-reminder-preview-tab">
                                    Rendered preview
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent value="plain">
                                <Textarea
                                    value={plainBody}
                                    onChange={(e) => setPlainBody(e.target.value)}
                                    rows={14}
                                    className="font-mono text-xs"
                                    data-testid="ncb-reminder-plain-input"
                                />
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Edits here update the fallback plain-text version only. If you want to
                                    change the formatted email the customer sees, use the HTML tab.
                                </p>
                            </TabsContent>
                            <TabsContent value="html">
                                <Textarea
                                    value={htmlBody}
                                    onChange={(e) => setHtmlBody(e.target.value)}
                                    rows={14}
                                    className="font-mono text-[11px]"
                                    data-testid="ncb-reminder-html-input"
                                />
                            </TabsContent>
                            <TabsContent value="preview">
                                <div
                                    className="border border-slate-200 rounded-md max-h-96 overflow-y-auto bg-white"
                                    data-testid="ncb-reminder-rendered-preview"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{ __html: htmlBody }}
                                />
                            </TabsContent>
                        </Tabs>

                        {guard?.within_window && (
                            <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                                <Label htmlFor="ncb-reminder-override" className="text-xs text-amber-800 cursor-pointer">
                                    Override 24-hour duplicate guard
                                </Label>
                                <Switch
                                    id="ncb-reminder-override"
                                    checked={override}
                                    onCheckedChange={setOverride}
                                    data-testid="ncb-reminder-override-toggle"
                                />
                            </div>
                        )}

                        {error && (
                            <Alert variant="destructive">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={loadPreview}
                        disabled={loading || sending}
                        data-testid="ncb-reminder-refresh-btn"
                    >
                        <RefreshCcw className="h-4 w-4 mr-1" />
                        Reload preview
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={sending}
                        data-testid="ncb-reminder-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!canSend}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="ncb-reminder-send-btn"
                    >
                        {sending ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</>
                        ) : (
                            <><CheckCircle className="h-4 w-4 mr-1" /> Approve &amp; Send</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default NCBReminderDialog;
